import i18nFactory from '@yandex-int/i18n';
import { compact, isUndefined } from 'lodash';
import { Error } from 'tslint/lib/error';

import { NoticeType, showNoticeMessage } from '../../actions/grid-screen';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import {
    CurrencyCode,
    DeleteSmartFilters,
    GdCampaignPlatform,
    GdRecommendationSign,
    GdSmartFilterBaseStatusInput,
    GdSmartFilterPrimaryStatus,
    GdSmartFilterTargetFunnel,
    UpdateSmartFilters
} from '../../typings/graphql-grid';
import { FeedFiltersAction } from '../../typings/grid';
import { SmartGroupBudgetCalculation } from '../../typings/groups';
import { EditGroupFilterItem } from '../../typings/groupsEditState';
import { ApiMethodExecutingError } from '../Errors';
import { getFormattedPrice } from '../FormatHelpers/FormatHelpers';
import { showFeedFiltersActionNoticeMessage } from '../gridItemsActionsHelpers/gridItemsActionsHelpers';
import { mapAutobudgetPriorityToString } from '../PhrasesHelpers/PhrasesHelpers';

import * as keyset from './FeedFiltersHelpers.i18n';

const i18n = i18nFactory(keyset);

export enum FeedFiltersModalShownView {
    groupsEdit = 'groupsEdit',
    filtersGrid = 'filtersGrid'
}

export const HIDDEN_CONDITION_FIELDS = ['available'];

export const feedFilterStatusInfoDictionary: Record<
    GdSmartFilterPrimaryStatus,
    string
> = {
    [GdSmartFilterPrimaryStatus.ACTIVE]: i18n('status-active'),
    [GdSmartFilterPrimaryStatus.STOPPED]: i18n('status-stopped')
};

const feedFilterPlatformTargetFunnelInfo: Record<
    string,
    { statusHint: string; warningMessage: string }
> = {
    [GdCampaignPlatform.SEARCH +
    GdSmartFilterTargetFunnel.PRODUCT_PAGE_VISIT]: {
        statusHint: i18n('status-hint-not-show'),
        warningMessage: i18n('unavailable-for-search')
    },
    [GdCampaignPlatform.SEARCH + GdSmartFilterTargetFunnel.SAME_PRODUCTS]: {
        statusHint: i18n('status-hint-show-not-everyone'),
        warningMessage: i18n('similar-goods-for-search')
    },
    [GdCampaignPlatform.BOTH + GdSmartFilterTargetFunnel.PRODUCT_PAGE_VISIT]: {
        statusHint: i18n('status-hint-only-for-context'),
        warningMessage: i18n('unavailable-for-search')
    },
    [GdCampaignPlatform.BOTH + GdSmartFilterTargetFunnel.SAME_PRODUCTS]: {
        statusHint: i18n('status-hint-show-not-everyone'),
        warningMessage: i18n('similar-goods-for-search')
    }
    // // todo @dimazoll - логика показа предупреждения про Ecommerce будет сделана в отдельной задаче DIRECT-95657
    // [GdCampaignPlatform.CONTEXT +
    // GdSmartFilterTargetFunnel.PRODUCT_PAGE_VISIT]: {
    //     statusHint: i18n('incorrect-ecommerce'),
    //     warningMessage: i18n('incorrect-ecommerce')
    // }
};

export const feedFiltersCriteriaSetTitlesDictionary: Record<
    GdSmartFilterBaseStatusInput,
    string
> = {
    [GdSmartFilterBaseStatusInput.ACTIVE]: i18n('status-active'),
    [GdSmartFilterBaseStatusInput.SUSPENDED]: i18n('status-stopped')
};

export function getFeedFilterStatusInfo(
    status: GdSmartFilterPrimaryStatus
): string {
    return feedFilterStatusInfoDictionary[status];
}

export const targetFunnelDictionary: Record<
    GdSmartFilterTargetFunnel,
    string
> = {
    [GdSmartFilterTargetFunnel.NEW_AUDITORY]: i18n('new-auditory'),
    [GdSmartFilterTargetFunnel.SAME_PRODUCTS]: i18n('same-products'),
    [GdSmartFilterTargetFunnel.PRODUCT_PAGE_VISIT]: i18n('product-page-visit')
};

export function getFunnelTypeName(
    funnelType: GdSmartFilterTargetFunnel
): string {
    return targetFunnelDictionary[funnelType];
}

function getFormattedPriceText(
    price?: number,
    currency?: CurrencyCode,
    label?: string
): string {
    const currencySymbol = currency ? mapStringToCurrencySymbol(currency) : '';

    if (isUndefined(price)) {
        return '';
    }

    return compact([
        label,
        getFormattedPrice(String(price)) || 0,
        currencySymbol
    ]).join(' ');
}

export function getFilterCpcPriceText(
    filter: EditGroupFilterItem,
    hasLabel: boolean,
    currency?: CurrencyCode
): string {
    return getFormattedPriceText(
        filter.priceCpc,
        currency,
        hasLabel ? GdRecommendationSign.CPC : ''
    );
}

export function getFilterCpaPriceText(
    filter: EditGroupFilterItem,
    hasLabel: boolean,
    currency?: CurrencyCode
): string {
    return getFormattedPriceText(
        filter.priceCpa,
        currency,
        hasLabel ? GdRecommendationSign.CPA : ''
    );
}

export function getFilterPriceText(
    filter: EditGroupFilterItem,
    budgetCalculation: SmartGroupBudgetCalculation,
    currency?: CurrencyCode
): string {
    const { autobudgetPriority } = filter;
    const emptyRateMessage = '—';

    const priceCpcText = getFilterCpcPriceText(filter, true, currency);
    const priceCpaText = getFilterCpaPriceText(filter, true, currency);

    switch (budgetCalculation) {
        case SmartGroupBudgetCalculation.FULL:
            if (!priceCpcText && !priceCpaText) {
                return emptyRateMessage;
            }

            return compact([priceCpcText, priceCpaText]).join(', ');
        case SmartGroupBudgetCalculation.CPC:
            return priceCpcText || emptyRateMessage;
        case SmartGroupBudgetCalculation.AUTO:
            return autobudgetPriority
                ? mapAutobudgetPriorityToString(autobudgetPriority)
                : emptyRateMessage;
        default:
            return emptyRateMessage;
    }
}

export function showFeedFiltersUpdateErrorMessage(
    error: ApiMethodExecutingError | Error
): void {
    showNoticeMessage(
        NoticeType.error,
        error instanceof ApiMethodExecutingError
            ? error.getMessage()
            : error.message
    );
}

export function showFeedFiltersUpdateSuccessMessage(
    data: UpdateSmartFilters,
    action: FeedFiltersAction
): void {
    const { updatedItems, validationResult } = data.updateFilters;
    const noUpdatedFilters =
        (validationResult && validationResult.warnings.length) || 0;

    showFeedFiltersActionNoticeMessage(
        action,
        updatedItems.filter(Boolean).length - noUpdatedFilters,
        updatedItems.length
    );
}

export function showFeedFiltersDeleteSuccessMessage(
    data: DeleteSmartFilters,
    action: FeedFiltersAction
): void {
    const { deletedItems } = data.deleteFilters;

    showFeedFiltersActionNoticeMessage(
        action,
        deletedItems.filter(Boolean).length,
        deletedItems.length
    );
}

export function getTargetFunnelWarningMessage(
    targetFunnel: GdSmartFilterTargetFunnel,
    platform?: GdCampaignPlatform
): string {
    const platformTargetFunnelInfo =
        feedFilterPlatformTargetFunnelInfo[`${platform}${targetFunnel}`];

    return platformTargetFunnelInfo
        ? platformTargetFunnelInfo.warningMessage
        : '';
}

export function getStatusHint(
    targetFunnel: GdSmartFilterTargetFunnel,
    platform?: GdCampaignPlatform
): string {
    const platformTargetFunnelInfo =
        feedFilterPlatformTargetFunnelInfo[`${platform}${targetFunnel}`];

    return platformTargetFunnelInfo ? platformTargetFunnelInfo.statusHint : '';
}
